import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { 
  Award, 
  Users, 
  Briefcase, 
  Clock, 
  Target, 
  TrendingUp,
  CheckCircle2,
  Download,
  Play
} from 'lucide-react';
import {
  coursePlans,
  certifications,
  curriculum,
  courseHighlights,
  learningOutcomes,
  toolsCovered,
  faqs
} from '../mockLearn';

const Learn = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  const highlightIcons = {
    Users, Award, Briefcase, Clock, Target, TrendingUp
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#fffaf1'}}>
      <Helmet>
        <title>Learn Digital Marketing - Professional Certification Program | EduSocial</title>
        <meta name="description" content="Master digital marketing with Google, Meta & LinkedIn certifications. 6-month professional program or 3-month foundation course. Learn SEO, SEM, Social Media, Content Marketing & more." />
        <link rel="canonical" href="https://edusocial.co.in/learn" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" style={{background: 'linear-gradient(135deg, #fffaf1 0%, #fff5e1 100%)'}}>
        <div className="container mx-auto text-center">
          <Badge className="mb-4 px-4 py-2" style={{backgroundColor: '#33c375', color: 'white'}}>
            🎓 Professional Certification Program
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{color: '#012726'}}>
            Master Digital Marketing with{' '}
            <span style={{color: '#33c375'}}>Industry Certifications</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Become a certified digital marketing professional with hands-on training from industry experts.
            Get certified by Google, Meta & LinkedIn while working on real client projects.
          </p>

          {/* Certification Logos */}
          <div className="flex flex-wrap justify-center items-center gap-8 mb-8">
            {certifications.map((cert, index) => (
              <div key={index} className="text-center">
                <div className="bg-white p-4 rounded-lg shadow-md mb-2">
                  <img src={cert.logo} alt={cert.name} className="h-12 w-auto mx-auto" />
                </div>
                <p className="text-xs text-gray-600">{cert.description}</p>
              </div>
            ))}
          </div>

          <Button 
            size="lg" 
            className="text-white px-8 py-6 text-lg"
            style={{backgroundColor: '#33c375'}}
          >
            <Play className="mr-2" size={20} />
            Watch Course Preview
          </Button>
        </div>
      </section>

      {/* Course Highlights */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              Why Choose This Program?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courseHighlights.map((highlight, index) => {
              const Icon = highlightIcons[highlight.icon];
              return (
                <Card key={index} className="border-2 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4"
                      style={{backgroundColor: '#33c375'}}
                    >
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-lg font-semibold mb-2" style={{color: '#012726'}}>
                      {highlight.title}
                    </h3>
                    <p className="text-gray-600 text-sm">{highlight.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#fffaf1'}}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              Choose Your Learning Path
            </h2>
            <p className="text-lg text-gray-600">Select the program that fits your career goals and timeline</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {coursePlans.map((plan) => (
              <Card 
                key={plan.id} 
                className={`relative border-2 hover:shadow-xl transition-all duration-300 ${
                  plan.recommended ? 'border-4' : ''
                }`}
                style={plan.recommended ? {borderColor: '#33c375'} : {}}
              >
                {plan.recommended && (
                  <div 
                    className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-white text-sm font-semibold"
                    style={{backgroundColor: '#33c375'}}
                  >
                    ⭐ MOST POPULAR
                  </div>
                )}
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold mb-2" style={{color: plan.color}}>
                      {plan.name}
                    </h3>
                    <p className="text-gray-600 mb-4">{plan.duration}</p>
                    <div className="text-4xl font-bold mb-2" style={{color: '#012726'}}>
                      {plan.price}
                    </div>
                    <p className="text-sm text-gray-600">or {plan.emi}</p>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle2 size={20} className="flex-shrink-0 mt-0.5" style={{color: '#33c375'}} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button 
                    className="w-full text-white py-6 text-lg"
                    style={{backgroundColor: plan.color}}
                    onClick={() => setSelectedPlan(plan.id)}
                  >
                    Enroll Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-center text-gray-600 mt-8">
            🎁 <strong>Limited Time Offer:</strong> Get 10% Early Bird Discount + Free Marketing Toolkit Worth ₹9,999
          </p>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              Course Curriculum
            </h2>
            <p className="text-lg text-gray-600">Comprehensive, industry-relevant curriculum designed by experts</p>
          </div>

          {/* 6-Month Curriculum */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-6 flex items-center" style={{color: '#33c375'}}>
              <Award className="mr-3" size={28} />
              6-Month Professional Program - All 12 Modules
            </h3>
            <Accordion type="single" collapsible className="space-y-4">
              {curriculum.sixMonths.map((module, index) => (
                <AccordionItem key={index} value={`module-${index}`} className="border-2 rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center space-x-4 text-left">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold"
                        style={{backgroundColor: '#012726'}}
                      >
                        {module.module}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg" style={{color: '#012726'}}>
                          {module.title}
                        </h4>
                        <p className="text-sm text-gray-600">{module.duration}</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <div className="pl-16">
                      <p className="font-semibold mb-2" style={{color: '#012726'}}>Topics Covered:</p>
                      <ul className="space-y-2 mb-4">
                        {module.topics.map((topic, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <CheckCircle2 size={16} className="flex-shrink-0 mt-1" style={{color: '#33c375'}} />
                            <span className="text-gray-700">{topic}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="bg-green-50 p-3 rounded-lg">
                        <p className="text-sm font-semibold" style={{color: '#33c375'}}>
                          💼 Project: {module.project}
                        </p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* 3-Month Curriculum */}
          <div>
            <h3 className="text-2xl font-bold mb-6 flex items-center" style={{color: '#012726'}}>
              <Target className="mr-3" size={28} />
              3-Month Foundation Program - First 6 Modules
            </h3>
            <Accordion type="single" collapsible className="space-y-4">
              {curriculum.threeMonths.map((module, index) => (
                <AccordionItem key={index} value={`short-${index}`} className="border-2 rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center space-x-4 text-left">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold"
                        style={{backgroundColor: '#012726'}}
                      >
                        {module.module}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg" style={{color: '#012726'}}>
                          {module.title}
                        </h4>
                        <p className="text-sm text-gray-600">{module.duration}</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <div className="pl-16">
                      <p className="font-semibold mb-2" style={{color: '#012726'}}>Topics Covered:</p>
                      <ul className="space-y-2 mb-4">
                        {module.topics.map((topic, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <CheckCircle2 size={16} className="flex-shrink-0 mt-1" style={{color: '#33c375'}} />
                            <span className="text-gray-700">{topic}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="bg-green-50 p-3 rounded-lg">
                        <p className="text-sm font-semibold" style={{color: '#33c375'}}>
                          💼 Project: {module.project}
                        </p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Learning Outcomes */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#fffaf1'}}>
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              What You'll Achieve
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {learningOutcomes.map((outcome, index) => (
              <div key={index} className="flex items-start space-x-3 p-4 bg-white rounded-lg shadow-sm">
                <CheckCircle2 size={24} className="flex-shrink-0" style={{color: '#33c375'}} />
                <span className="text-gray-700">{outcome}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools Covered */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              Tools & Platforms You'll Master
            </h2>
            <p className="text-lg text-gray-600">Hands-on experience with 20+ industry-standard tools</p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {toolsCovered.map((tool, index) => (
              <Badge 
                key={index} 
                className="px-4 py-2 text-sm"
                style={{backgroundColor: '#e8f5ee', color: '#012726', border: '1px solid #33c375'}}
              >
                {tool}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#fffaf1'}}>
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`faq-${index}`} className="bg-white border-2 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold" style={{color: '#012726'}}>
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 text-white" style={{background: 'linear-gradient(135deg, #012726 0%, #014d49 100%)'}}>
        <div className="container mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Start Your Digital Marketing Career Today
          </h2>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto mb-8">
            Join 500+ students who transformed their careers with EduSocial's digital marketing program
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              size="lg" 
              className="text-white px-8 py-6 text-lg"
              style={{backgroundColor: '#33c375'}}
            >
              Enroll Now - Limited Seats
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 text-white px-8 py-6 text-lg"
              style={{borderColor: 'white'}}
            >
              <Download className="mr-2" size={20} />
              Download Course Brochure
            </Button>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <Badge className="px-4 py-2" style={{backgroundColor: 'rgba(255,255,255,0.2)', color: 'white'}}>
              EMI Starting ₹16,666/month
            </Badge>
            <Badge className="px-4 py-2" style={{backgroundColor: 'rgba(255,255,255,0.2)', color: 'white'}}>
              3 Industry Certifications
            </Badge>
            <Badge className="px-4 py-2" style={{backgroundColor: 'rgba(255,255,255,0.2)', color: 'white'}}>
              100% Job Assistance
            </Badge>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Learn;
